import {
  type ThunkDispatch,
  type UnknownAction,
  createAsyncThunk
} from "@reduxjs/toolkit"
import i18n from "i18next"

import { errorSave } from "../../../redux/errors.slice"
import { type BaseFilters, type BasePagination } from "../../../types/api.ts"
import {
  firstFieldError,
  getResponseMessage
} from "../../../utils/handlers/get-response-message.ts"
import { unknownError } from "../../../utils/handlers/handle-error"
import { handleTemplateImportError } from "../../../utils/handlers/handle-template-import-error.tsx"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { type Lead } from "../../Lead/types/lead.types.ts"
import {
  UNPAID_INVOICES_ACKNOWLEDGEMENT,
  unpaidInvoicesFrom
} from "../api/pending-commitments.ts"
import * as services from "../api/services"
import type { ImportProjectsData } from "../api/services.types.ts"
import { projectsSliceName } from "./projects.constants.ts"
import {
  type ProjectBulkResult,
  type ProjectData,
  type ProjectFilters
} from "./projects.types.ts"

export enum ProjectFetchType {
  LIST,
  GRID
}

export const fetchProjects = createAsyncThunk<
  { type: ProjectFetchType; data: ProjectData; isMore?: boolean },
  { type: ProjectFetchType; filters: ProjectFilters; isMore?: boolean }
>(
  `${projectsSliceName}/fetchProjects`,
  async ({ type, isMore, filters }, { dispatch, rejectWithValue }) => {
    const response = await services.getProjects(filters)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return { type, isMore, data: response?.data as ProjectData }
  }
)

export const deleteProject = createAsyncThunk<{ id: string }, string>(
  `${projectsSliceName}/deleteProject`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteProject(id)

    if (!response.ok) {
      // The delete refusal is raised as a bare `{type, message, detail}` body,
      // not nested under `details`, so reading `details` alone showed the
      // generic error instead of the reason the guard gave.
      const body = response?.data as
        | { details?: unknown; message?: string }
        | undefined
      dispatch(
        errorSave(body?.details || (body?.message ? body : unknownError))
      )
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Project deleted successfully.")

    return { id }
  }
)

export const restoreProject = createAsyncThunk<{ id: string }, string>(
  `${projectsSliceName}/restoreProject`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.restoreProject(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, i18n.t("projectArchive.restored"))

    return { id }
  }
)

export const importProjects = createAsyncThunk<
  {
    created_count: number
    skipped_count: number
    errors: unknown[]
  },
  File
>(
  `${projectsSliceName}/importProjects`,
  async (file, { dispatch, rejectWithValue }) => {
    const response = await services.importProjects(file)

    if (!response.ok) {
      const blob = response.data as Blob
      const text = await blob.text()
      const data = JSON.parse(text)

      dispatch(errorSave(data?.detail || data?.details))
      return rejectWithValue(data || { errors: "Unknown error" })
    }

    const isError = handleTemplateImportError(
      response,
      "import-projects-template.xlsx"
    )

    if (isError) {
      dispatch(
        errorSave("Import completed with errors. Check the downloaded file.")
      )
      return rejectWithValue({ errors: "File with corrections returned" })
    }

    showResponseMessage(response, "Projects imported data successfully.")
    return response.data as ImportProjectsData
  }
)

export const fetchImportProjectsTemplates = createAsyncThunk<Blob>(
  `${projectsSliceName}/fetchImportProjectsTemplates`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getImportProjectsTemplates()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data as Blob
  }
)

export const fetchProjectsFilterOptions = createAsyncThunk<any>(
  `${projectsSliceName}/fetchFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectsFilterOptions()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response?.data
  }
)

type ProjectsStateReader = () => {
  projects: {
    fetchingFilterOptions: boolean
    filterOptions: { project_status?: unknown[] } | null
  }
}

// The options are the same for every project, so a screen that only needs them
// to be there reads the ones already loaded or in flight.
export const loadProjectsFilterOptions =
  () =>
  (
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
    getState: ProjectsStateReader
  ) => {
    const { fetchingFilterOptions, filterOptions } = getState().projects
    if (fetchingFilterOptions || filterOptions?.project_status?.length) return
    return dispatch(fetchProjectsFilterOptions())
  }

export const fetchLeadApproval = createAsyncThunk<
  BasePagination<Lead>,
  Pick<BaseFilters, "page" | "page_size">
>(
  `${projectsSliceName}/fetchLeadApproval`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getLeadsApproval(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response?.data as BasePagination<Lead>
  }
)

// CONVERT LEAD TO PROJECT
export const approveLead = createAsyncThunk<
  any,
  {
    leadId: string
    responsibleId: string
    applyFields?: Record<string, unknown>
  }
>(
  `${projectsSliceName}/approveLead`,
  async (
    { leadId, responsibleId, applyFields },
    { dispatch, rejectWithValue }
  ) => {
    const response = await services.approveLead(
      leadId,
      responsibleId,
      applyFields
    )

    if (!response.ok) {
      // The endpoint answers with `detail` or a field error such as
      // `{project_type: [...]}`; reading only `details` discarded both and
      // left the user with a bare failure.
      dispatch(
        errorSave(
          getResponseMessage(response?.data) ||
            firstFieldError(response?.data) ||
            unknownError
        )
      )
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Lead approved.")

    return response?.data
  }
)

/** Pulls a human-readable reason out of whatever error envelope the API returned. */
// The server's own unpaid-invoices text is a question ("This project has 2
// unpaid invoices."), which reads as no reason at all in a failure list, so it
// is replaced by one worded for the action that was refused.
type UnpaidFailureKey =
  | "projectUnpaid.bulkStaleFailure"
  | "projectUnpaid.bulkUnpaidFailure"

const projectBulkErrorMessage = (
  data: unknown,
  unpaidKey: UnpaidFailureKey
): string | undefined => {
  if (!data || typeof data !== "object") return undefined
  const unpaid = unpaidInvoicesFrom(data)
  if (unpaid) {
    return i18n.t(unpaidKey, { count: unpaid.unpaidInvoiceCount })
  }
  const body = data as Record<string, unknown>
  const detail = (body.details ?? body) as Record<string, unknown>
  const candidates = [
    detail?.message,
    detail?.detail,
    body?.detail,
    body?.message
  ]
  return candidates.find((value): value is string => typeof value === "string")
}

type ProjectBulkOutcome = {
  id: string
  ok: boolean
  message?: string
  unpaidInvoiceCount?: number
}

const refusedOutcome = (
  id: string,
  data: unknown,
  unpaidKey: UnpaidFailureKey
): ProjectBulkOutcome => {
  const unpaid = unpaidInvoicesFrom(data)
  return {
    id,
    ok: false,
    message: projectBulkErrorMessage(data, unpaidKey),
    ...(unpaid ? { unpaidInvoiceCount: unpaid.unpaidInvoiceCount } : {})
  }
}

const summarizeProjectBulk = (
  outcomes: ProjectBulkOutcome[],
  fallback: string
): ProjectBulkResult => ({
  requested: outcomes.length,
  succeeded: outcomes
    .filter((outcome) => outcome.ok)
    .map((outcome) => outcome.id),
  failed: outcomes
    .filter((outcome) => !outcome.ok)
    .map((outcome) => ({
      id: outcome.id,
      message: outcome.message || fallback,
      ...(outcome.unpaidInvoiceCount
        ? { unpaidInvoiceCount: outcome.unpaidInvoiceCount }
        : {})
    }))
})

type BulkProjectPatch = {
  project_status?: number
  project_managers?: { id: string; is_main: boolean }[]
}

// Projects have no bulk endpoint, so each bulk action fans the existing
// per-project call out in parallel and aggregates the outcome. Deliberately no
// `errorSave` here — a per-item failure is shown in the bulk bar, not the
// global toast — and no refetch; the list page re-reads once after the batch.
export const bulkPatchProjects = createAsyncThunk<
  ProjectBulkResult,
  {
    ids: string[]
    data: BulkProjectPatch
    /** The projects the user agreed to close with unpaid invoices. */
    acknowledgeUnpaidInvoicesFor?: string[]
  }
>(
  `${projectsSliceName}/bulkPatchProjects`,
  async ({ ids, data, acknowledgeUnpaidInvoicesFor = [] }) => {
    const outcomes = await Promise.all(
      ids.map(async (id) => {
        try {
          const response = await services.patchProject(id, {
            ...data,
            ...(acknowledgeUnpaidInvoicesFor.includes(id)
              ? UNPAID_INVOICES_ACKNOWLEDGEMENT
              : {})
          } as Parameters<typeof services.patchProject>[1])
          return response.ok
            ? { id, ok: true }
            : refusedOutcome(
                id,
                response.data,
                "projectUnpaid.bulkStaleFailure"
              )
        } catch {
          return { id, ok: false } as ProjectBulkOutcome
        }
      })
    )

    return summarizeProjectBulk(outcomes, "This project could not be updated.")
  }
)

export const bulkArchiveProjects = createAsyncThunk<
  ProjectBulkResult,
  { ids: string[] }
>(`${projectsSliceName}/bulkArchiveProjects`, async ({ ids }) => {
  const outcomes = await Promise.all(
    ids.map(async (id) => {
      try {
        const response = await services.archiveProject({ id })
        return response.ok
          ? { id, ok: true }
          : refusedOutcome(id, response.data, "projectUnpaid.bulkUnpaidFailure")
      } catch {
        return { id, ok: false } as ProjectBulkOutcome
      }
    })
  )

  return summarizeProjectBulk(outcomes, "This project could not be archived.")
})
